import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth'

// Lazy load components
const DashboardLayout = () => import('../layouts/DashboardLayout.vue')
const SurveyLayout = () => import('../layouts/SurveyLayout.vue')
const LoginPage = () => import('../views/LoginPage.vue')
const RatingManagement = () => import('../views/RatingManagement.vue')

const routes = [
  {
    path: '/',
    redirect: '/survey'
  },
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
    meta: { 
      requiresAuth: false,
      layout: null 
    }
  },
  // Dashboard Routes
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',  // dashboard page
        name: 'dashboard',
        component: () => import('../views/DashboardView.vue')
      }
    ]
  },
  {
    path: '/dashboard/reviews',  // Reviews page
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'reviews',
        component: () => import('../views/ReviewsPage.vue')
      }
    ]
  },
  // Rating management with DashboardLayout
  {
    path: '/rating-management',
    name: 'RatingManagement',
    component: DashboardLayout,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: '',
        name: 'rating-management',
        component: RatingManagement
      }
    ]
  },
  // Survey Routes
  {
    path: '/survey',
    component: SurveyLayout,
    meta: { 
      requiresAuth: false,
      layout: 'survey'
    },
    children: [
      {
        path: '',
        name: 'survey',
        component: () => import('../views/SurveyView.vue')
      },
      {
        path: 'rating/:id/details',
        name: 'rating-details',
        component: () => import('../views/RatingView.vue'),
        props: true,
        meta: { hideNav: true }
      }
    ]
  },
  // Follow-up H+3: kirim (blast) dan feedback dipisah
  {
    path: '/follow-up',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/follow-up/after-service' },
      {
        path: 'terima-kasih',
        name: 'thank-you',
        component: () => import('../views/ThankYouView.vue')
      },
      {
        path: 'after-service',
        name: 'reminder-h3',
        component: () => import('../views/ReminderDashboard.vue')
      },
      {
        path: 'feedback',
        name: 'feedback-h3',
        component: () => import('../views/H3FeedbackView.vue')
      }
    ]
  },
  // Reminder servis 3 & 6 bulan: satu halaman per jenis
  {
    path: '/reminders',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/reminders/3-bulan' },
      { path: 'long-term', redirect: '/reminders/3-bulan' },
      {
        path: '3-bulan',
        name: 'reminder-3-bulan',
        component: () => import('../views/LongTermReminderDashboard.vue'),
        props: { reminderType: '3_months' }
      },
      {
        path: '6-bulan',
        name: 'reminder-6-bulan',
        component: () => import('../views/LongTermReminderDashboard.vue'),
        props: { reminderType: '6_months' }
      }
    ]
  },
  // Dashboard membership (staf)
  {
    path: '/membership',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'membership', component: () => import('../views/MembershipDashboard.vue') }
    ]
  },
  // Laporan rekomendasi servis yang ditolak pelanggan (alasan & catatan)
  {
    path: '/rekomendasi-ditolak',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'recommendation-rejections', component: () => import('../views/RecommendationRejectionReport.vue') }
    ]
  },
  // Cek membership mandiri pelanggan: halaman publik berdiri sendiri, tanpa menu.
  {
    path: '/member',
    name: 'member-check',
    component: () => import('../views/MemberCheckView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Cek Member' }
  },
  // Public Feedback Routes
  {
    path: '/feedback/:id',
    name: 'public-feedback',
    component: SurveyLayout,
    props: (route) => ({ 
      orderId: route.params.id,
      database: route.query.db 
    }),
    meta: { 
      requiresAuth: false,
      layout: 'survey'
    },
    children: [{
      path: '',
      name: 'public-feedback-form',
      component: () => import('../views/FeedbackView.vue'),
      meta: { hideNav: true }
    }]
  },
  // Catch-all route for 404 handling
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('../views/NotFound.vue'),
    meta: {
      requiresAuth: false,
      layout: null
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Add scroll behavior for better UX
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Enhanced navigation guard with better logging and meta handling
router.beforeEach((to, from, next) => {
  const isAuthenticated = localStorage.getItem('uid')
  const authStore = useAuthStore()

  console.log('🔒 Navigation Guard:', {
    to: {
      path: to.path,
      name: to.name,
      meta: to.meta
    },
    from: from.path,
    isAuthenticated: !!isAuthenticated,
    requiresAuth: to.meta.requiresAuth
  })

  // Check authentication requirement
  if (to.meta.requiresAuth && !isAuthenticated) {
    console.log('❌ Access denied: Authentication required')
    next('/login')
    return
  }

  // Redirect authenticated users away from login
  if (to.path === '/login' && isAuthenticated) {
    console.log('✅ Already authenticated, redirecting to survey')
    next('/survey')
    return
  }

  // Set document title based on route meta
  if (to.meta.title) {
    document.title = `${to.meta.title} - PITCAR`
  } else {
    document.title = 'PITCAR - Service Management'
  }

  console.log('✅ Navigation allowed')
  next()
})

// Setelah rilis, file halaman versi lama sudah tidak ada di server. Bila
// membuka menu gagal karena itu, muat ulang sekali ke versi terbaru.
const CHUNK_RELOAD_KEY = 'pitscore:chunk-reload'
router.onError((error, to) => {
  const pesan = String(error?.message || error)
  if (!/dynamically imported module|Importing a module script failed|Loading chunk|Unable to preload CSS/i.test(pesan)) return
  let sudah = null
  try { sudah = sessionStorage.getItem(CHUNK_RELOAD_KEY) } catch { /* abaikan */ }
  if (sudah === to?.fullPath) return
  try { sessionStorage.setItem(CHUNK_RELOAD_KEY, to?.fullPath || '') } catch { /* abaikan */ }
  window.location.assign(to?.fullPath || window.location.href)
})
router.afterEach(() => {
  try { sessionStorage.removeItem(CHUNK_RELOAD_KEY) } catch { /* abaikan */ }
})

// After navigation guard for additional setup
router.afterEach((to, from) => {
  console.log('📍 Navigation completed:', {
    to: to.path,
    from: from.path
  })
})

export default router